import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  const isLoginRoute = config.url?.includes('/login');
  if (token && !isLoginRoute) {
    config.headers.Authorization = `Bearer ${token}`;
    config.params = { ...config.params, authorization: `Bearer ${token}` };
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config;
    // Retry once on network errors or 5xx (not on 401/403/4xx)
    if (!config._retried && (!err.response || err.response.status >= 500)) {
      config._retried = true;
      await new Promise(r => setTimeout(r, 500));
      return api(config);
    }
    // Only force logout on explicit 401 from non-auth endpoints
    if (err.response?.status === 401) {
      const token = localStorage.getItem('token');
      const isLoginRoute = config?.url?.includes('/login');
      if (token && !isLoginRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  employeeLogin: (phone, password) => api.post('/auth/employee/login', { phone, password }),
  adminLogin: (email, password) => api.post('/auth/admin/login', { email, password }),
  rmLogin: (identifier, password) => api.post('/auth/rm/login', { identifier, password }),
  getProfile: () => api.get('/auth/profile'),
  updateMyPhoto: (profile_photo) => api.put('/users/me/photo', { profile_photo }),
  recordConsent: (accepted) => api.post('/auth/consent', { accepted }),
  changePassword: (current_password, new_password) => api.post('/auth/change-password', { current_password, new_password }),
  adminChangePassword: (current_password, new_password) => api.post('/auth/admin/change-password', { current_password, new_password }),
};

export const attendanceAPI = {
  checkin: (data) => api.post('/attendance/checkin', data),
  checkout: (data) => api.post('/attendance/checkout', data),
  history: (page = 1) => api.get('/attendance/history', { params: { page } }),
  monthly: (month) => api.get('/attendance/monthly', { params: month ? { month } : {} }),
  today: () => api.get('/attendance/today'),
  canCheckin: () => api.get('/attendance/can-checkin'),
  myLateStats: (month) => api.get('/attendance/my-late-stats', { params: month ? { month } : {} }),
  ledger: (limit = 30) => api.get('/attendance/ledger', { params: { limit } }),
};

export const lateSanctionAPI = {
  request: (attendance_id, reason) => api.post('/late-sanctions/request', { attendance_id, reason }),
  myRequests: () => api.get('/late-sanctions/my-requests'),
};

export const workPermissionAPI = {
  request: (data) => api.post('/work-permissions/request', data),
  myRequests: () => api.get('/work-permissions/my-requests'),
};

export const locationAPI = {
  log: (data) => api.post('/location/log', data),
  myLogs: (date) => api.get('/location/my-logs', { params: date ? { date } : {} }),
};

export const adminAPI = {
  getEmployees: (status, page = 1, limit = 50, branch) => api.get('/admin/employees', { params: { ...(status ? { status } : {}), page, limit, ...(branch ? { branch } : {}) } }),
  createEmployee: (data) => api.post('/admin/employees', data),
  deleteEmployee: (id) => api.delete(`/admin/employees/${id}`),
  restoreEmployee: (id) => api.patch(`/admin/employees/${id}/restore`),
  getDeletionLogs: () => api.get('/admin/deletion-logs'),
  importEmployeesCSV: (file_content) => api.post('/admin/employees/import-csv', { file_content }),
  getEmployeeCSVTemplate: () => api.get('/admin/employees/csv-template', { responseType: 'blob' }),
  updateEmployeeStatus: (id, status) => api.patch(`/admin/employees/${id}/status`, { status }),
  updateEmployeeSchedule: (id, data) => api.put(`/admin/employees/${id}/schedule`, data),
  getEmployeePassword: (id) => api.get(`/admin/employees/${id}/password`),
  resetEmployeePassword: (id, newPassword) => api.put(`/admin/employees/${id}/password`, { new_password: newPassword }),
  updateEmployeeDetails: (id, data) => api.put(`/admin/employees/${id}/details`, data),
  updateEmployeeZone: (id, zone_ids) => api.put(`/admin/employees/${id}/zone`, { zone_ids }),
  getDashboard: (date, branch) => api.get('/admin/dashboard', { params: { ...(date ? { date } : {}), ...(branch ? { branch } : {}) } }),
  getEmployeeAttendance: (id, page = 1) => api.get(`/admin/attendance/employee/${id}`, { params: { page } }),
  deleteAttendance: (id) => api.delete(`/admin/attendance/${id}`),
  getLocationLogs: (employeeId, date) => api.get(`/admin/location-logs/${employeeId}`, { params: date ? { date } : {} }),
  exportAttendance: (date, employeeId) => {
    const params = {};
    if (date) params.date = date;
    if (employeeId) params.employee_id = employeeId;
    const token = localStorage.getItem('token');
    if (token) params.authorization = `Bearer ${token}`;
    return api.get('/admin/attendance/export', { params, responseType: 'blob' });
  },
  getZones: () => api.get('/admin/zones'),
  createZone: (data) => api.post('/admin/zones', data),
  updateZone: (id, data) => api.put(`/admin/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/admin/zones/${id}`),
  assignEmployees: (zoneId, employeeIds) => api.put(`/admin/zones/${zoneId}/employees`, { employee_ids: employeeIds }),
  getLeaves: (status, page = 1, limit = 50, branch) => api.get('/admin/leaves', { params: { ...(status ? { status } : {}), page, limit, ...(branch ? { branch } : {}) } }),
  updateLeaveStatus: (id, status) => api.patch(`/admin/leaves/${id}/status`, { status }),
  cancelLeave: (id) => api.patch(`/admin/leaves/${id}/cancel`),
  getHolidays: (year) => api.get('/admin/holidays', { params: year ? { year } : {} }),
  createHoliday: (data) => api.post('/admin/holidays', data),
  deleteHoliday: (id) => api.delete(`/admin/holidays/${id}`),
  importHolidaysCSV: (file_content) => api.post('/admin/holidays/import-csv', { file_content }),
  getHolidayCategories: () => api.get('/admin/holiday-categories'),
  createHolidayCategory: (name) => api.post('/admin/holiday-categories', { name }),
  renameHolidayCategory: (id, name) => api.put(`/admin/holiday-categories/${id}`, { name }),
  deleteHolidayCategory: (id) => api.delete(`/admin/holiday-categories/${id}`),
  getHolidayCSVTemplate: () => api.get('/admin/holidays/csv-template', { responseType: 'blob' }),
  getAnalytics: (month, branch) => api.get('/admin/analytics', { params: { ...(month ? { month } : {}), ...(branch ? { branch } : {}) } }),
  exportAnalytics: (params) => api.get('/admin/analytics/export', { params, responseType: 'blob' }),
  exportBackup: () => api.get('/admin/backup/export', { responseType: 'blob', timeout: 120000 }),
  getLateSanctions: (status, page = 1, limit = 50, branch) => api.get('/admin/late-sanctions', { params: { ...(status ? { status } : {}), page, limit, ...(branch ? { branch } : {}) } }),
  updateLateSanctionStatus: (id, status) => api.patch(`/admin/late-sanctions/${id}/status`, { status }),
  getWorkPermissions: (status, page = 1, limit = 50, branch) => api.get('/admin/work-permissions', { params: { ...(status ? { status } : {}), page, limit, ...(branch ? { branch } : {}) } }),
  updateWorkPermissionStatus: (id, status) => api.patch(`/admin/work-permissions/${id}/status`, { status }),
  getSettings: () => api.get('/admin/settings'),
  updateRetention: (retention_months) => api.put('/admin/settings/retention', { retention_months }),
  updateDefaultSchedule: (checkin, checkout, gracePeriod) => api.put('/admin/settings/default-schedule', null, {
    params: { default_checkin_time: checkin || '', default_checkout_time: checkout || '', grace_period_minutes: gracePeriod ?? 15 }
  }),
  runDataCleanup: () => api.post('/admin/data-cleanup'),
  getAuditLogs: (page = 1, limit = 50) => api.get(`/admin/audit-logs?page=${page}&limit=${limit}`),
  getDailySummary: (date, branch) => api.get('/admin/daily-summary', { params: { ...(date ? { date } : {}), ...(branch ? { branch } : {}) } }),
  getAttendanceSelfie: (recordId, field = 'selfie') => api.get(`/admin/attendance-selfie/${recordId}`, { params: { field } }),
  getEmployeeMonthly: (id, month) => api.get(`/admin/attendance/monthly/${id}`, { params: month ? { month } : {} }),
  getBranches: () => api.get('/admin/branches'),
  createBranch: (data) => api.post('/admin/branches', data),
  updateBranch: (id, data) => api.put(`/admin/branches/${id}`, data),
  deleteBranch: (id) => api.delete(`/admin/branches/${id}`),
  getDesignations: () => api.get('/admin/designations'),
  getDesignationsManage: () => api.get('/admin/designations/manage'),
  createDesignation: (name) => api.post('/admin/designations', null, { params: { name } }),
  updateDesignation: (id, name) => api.patch(`/admin/designations/${id}`, null, { params: { name } }),
  deleteDesignation: (id) => api.delete(`/admin/designations/${id}`),
  getAuditTrail: (params) => api.get('/admin/audit-trail', { params }),
  getWorkPermits: (params) => api.get('/admin/work-permits', { params }),
  editWorkPermit: (id, data) => api.patch(`/admin/work-permits/${id}`, null, { params: data }),
  deleteWorkPermit: (id) => api.delete(`/admin/work-permits/${id}`),
  getAttendanceOverridesPending: () => api.get('/admin/attendance-overrides/pending'),
  getAttendanceOverrides: (status) => api.get('/admin/attendance-overrides', { params: status ? { status } : {} }),
  decideOverride: (id, status, remark) => api.patch(`/admin/attendance-overrides/${id}/decision`, null, { params: { status, remark: remark || '' } }),
  logout: () => api.post('/auth/logout'),
  getRMs: () => api.get('/admin/rms'),
  createRM: (data) => api.post('/admin/rms', data),
  updateRM: (id, data) => api.put(`/admin/rms/${id}`, data),
  deleteRM: (id) => api.delete(`/admin/rms/${id}`),
};

export const rmAPI = {
  getDashboard: () => api.get('/rm/dashboard'),
  getStaffAttendance: (date) => api.get('/rm/staff-attendance', { params: date ? { date } : {} }),
  updateLeave: (id, status) => api.patch(`/rm/leaves/${id}/status`, { status }),
  updateSanction: (id, status) => api.patch(`/rm/late-sanctions/${id}/status`, { status }),
  updatePermit: (id, status) => api.patch(`/rm/work-permissions/${id}/status`, { status }),
  updateCompOff: (id, status) => api.patch(`/rm/comp-off/${id}/status`, { status }),
  getApprovals: (month) => api.get('/rm/approvals', { params: month ? { month } : {} }),
  exportApprovals: (month) => api.get('/rm/approvals/export', { params: month ? { month } : {}, responseType: 'blob' }),
  createOverride: (data) => api.post('/rm/attendance-override', data),
  getMyOverrides: () => api.get('/rm/attendance-overrides/my'),
};

export const leaveAPI = {
  request: (data) => api.post('/leaves/request', data),
  myLeaves: (page = 1) => api.get('/leaves/my-leaves', { params: { page } }),
  myBalance: () => api.get('/leaves/my-balance'),
};

export const compOffAPI = {
  balance: () => api.get('/comp-off/balance'),
  request: (data) => api.post('/comp-off/request', data),
  myRequests: () => api.get('/comp-off/my-requests'),
};

export const sharedAPI = {
  onLeaveToday: () => api.get('/shared/on-leave-today'),
};

export const notificationAPI = {
  getAll: (limit = 20) => api.get('/notifications', { params: { limit } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export const holidayAPI = {
  getPublic: (year) => api.get('/holidays', { params: year ? { year } : {} }),
};

export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (endpoint) => api.delete('/push/unsubscribe', { params: endpoint ? { endpoint } : {} }),
};

export default api;
